import { AfterViewInit, Component, Inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogClose, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { UiAtomButtonComponent } from '../../atoms/ui-atom-button/ui-atom-button.component';
import { UiMoleculeInputComponent } from '../ui-molecule-input/ui-molecule-input.component';
import { UiAtomTextComponent } from '../../atoms/ui-atom-text/ui-atom-text.component';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';

const COUNTRY_NAME_MAX = 50;

export interface CountryUpsertData {
  title: string;
  message?: string;
  defaults?: {
    name?: string;
  };
  mode?: 'create' | 'edit';
  country?: any;
}

export interface CountryDialogResult {
  mode: 'create' | 'edit';
  payload: any;
  countryId?: string;
}

@Component({
  selector: 'ui-molecule-dialog-country-upsert',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatDialogModule,
    UiMoleculeInputComponent,
    UiAtomButtonComponent,
    UiAtomTextComponent,
    MatDialogClose,
    TranslatePipe,
  ],
  templateUrl: './ui-molecule-dialog-country-upsert.component.html',
  styleUrl: './ui-molecule-dialog-country-upsert.component.scss',
})
export class CountryUpsertDialogComponent implements AfterViewInit {
  readonly nameMax = COUNTRY_NAME_MAX;
  form: FormGroup;
  isLoading = false;
  errorMessage = '';
  formSubmitted = false;

  constructor(
    private fb: FormBuilder,
    private translate: TranslateService,
    public dialogRef: MatDialogRef<CountryUpsertDialogComponent, CountryDialogResult>,
    @Inject(MAT_DIALOG_DATA) public data: CountryUpsertData,
  ) {
    this.form = this.fb.group({
      name: ['', [Validators.required, Validators.maxLength(COUNTRY_NAME_MAX)]],
    });
  }

  get nameError(): string | null {
    const c = this.form.get('name');
    if (!c || (!c.touched && !c.dirty && !this.formSubmitted)) return null;
    if (c.hasError('required')) {
      return this.translate.instant('countries.form.name.error.required');
    }
    if (c.hasError('maxlength')) {
      return this.translate.instant('countries.form.name.error.maxLength', { max: this.nameMax });
    }
    return null;
  }

  ngAfterViewInit(): void {
    // Cargar datos por defecto o del país a editar
    if (this.data.defaults) {
      this.form.patchValue(this.data.defaults);
    }

    if (this.data.mode === 'edit' && this.data.country) {
      this.form.patchValue({
        name: this.data.country.name,
      });
    }
  }

  submit(): void {
    this.formSubmitted = true;

    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }

    this.isLoading = true;
    this.errorMessage = '';

    const formData = this.form.getRawValue();

    if (this.data.mode === 'edit' && this.data.country) {
      // Modo edición
      const payload = {
        name: formData.name,
      };
      this.dialogRef.close({ mode: 'edit', payload: payload, countryId: this.data.country.id });
    } else {
      // Modo creación
      const payload = {
        name: formData.name,
      };
      this.dialogRef.close({ mode: 'create', payload: payload });
    }
  }

  onCancel(): void {
    this.dialogRef.close();
  }
}
